<script setup>
import { defineAsyncComponent } from "vue";
import {
  CalendarDaysIcon,
  DocumentTextIcon,
  BanknotesIcon,
  ClipboardDocumentCheckIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  UserGroupIcon,
  ChartBarIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  DeviceTabletIcon,
  DocumentCheckIcon,
  ScaleIcon as ScaleIconOutline,
} from "@heroicons/vue/24/outline";
import {
  HeartIcon,
  FaceSmileIcon,
  SparklesIcon,
  ChatBubbleLeftRightIcon as ChatBubbleLeftRightIconSolid,
  ScaleIcon,
  MicrophoneIcon,
  EyeIcon,
  BeakerIcon,
  PhotoIcon,
  UserGroupIcon as UserGroupIconSolid,
} from "@heroicons/vue/24/solid";
import { useSeo } from "@/composables/useSeo";
import FaqPanel from "@/components/ui/FAQ.vue";
import SolutionHero from "@/components/ui/SolutionHero.vue";
import imageBg from "@/assets/solutions/kaliclinica.webp";
import visaoGeralImg from "@/assets/visaogeralclinica.webp";
import gestaoTributaria from "@/assets/gestao-tributaria.webp";

const CTA = defineAsyncComponent(() => import("@/components/home/CTA.vue"));
const Clients = defineAsyncComponent(() => import("@/components/home/Clients.vue"));

// Revela o elemento com fade+slide assim que ele entra na viewport ao rolar a página.
const vReveal = {
  mounted(el) {
    el.classList.add("reveal-init");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("reveal-visible");
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
  },
};

const navLinks = [
  { label: "Visão geral", href: "#visao-geral" },
  { label: "Segmentos", href: "#segmentos" },
  { label: "Funcionalidades", href: "#funcionalidades" },
  { label: "Fiscal", href: "#fiscal" },
  { label: "Módulos", href: "#modulos" },
  { label: "Depoimentos", href: "#depoimentos" },
  { label: "Dúvidas", href: "#duvidas" },
];

function openDemoForSegmento() {
  window.dispatchEvent(
    new CustomEvent("open-demo-modal", { detail: { segmento: "Clínica médica / Odontológica" } }),
  );
}

// Conteúdo abaixo é baseado no ebook oficial "Kali Gestão Clínica" (docs/books/KALI CLINICA - EBOOK.pdf).
const segmentos = [
  { icon: HeartIcon, label: "Clínicas médicas" },
  { icon: FaceSmileIcon, label: "Clínicas odontológicas" },
  { icon: SparklesIcon, label: "Estética facial e corporal" },
  { icon: ChatBubbleLeftRightIconSolid, label: "Psicologia e terapias" },
  { icon: ScaleIcon, label: "Nutrição" },
  { icon: MicrophoneIcon, label: "Fonoaudiologia" },
  { icon: EyeIcon, label: "Oftalmologia" },
  { icon: BeakerIcon, label: "Laboratórios de análises" },
  { icon: PhotoIcon, label: "Centros de diagnóstico" },
  { icon: UserGroupIconSolid, label: "Consultórios multiprofissionais" },
];

const destaques = [
  {
    eyebrow: "Agenda",
    title: "Agenda inteligente e otimizada",
    text: "Agendamentos recorrentes, registro de faltas e comunicação com o paciente via WhatsApp.",
    icon: CalendarDaysIcon,
    tint: "bg-kali-blue-100 text-kali-blue-700",
  },
  {
    eyebrow: "Prontuário",
    title: "Prontuário eletrônico completo",
    text: "Registre informações, anexe imagens e exames, e compartilhe com outros profissionais da clínica.",
    icon: DocumentTextIcon,
    tint: "bg-emerald-100 text-emerald-700",
  },
  {
    eyebrow: "Financeiro",
    title: "Controle financeiro sem burocracia",
    text: "Menos papéis e planilhas: acompanhe receita, despesas e relatórios de forma automática.",
    icon: BanknotesIcon,
    tint: "bg-amber-100 text-amber-700",
  },
  {
    eyebrow: "Orçamentos",
    title: "Do orçamento ao atendimento",
    text: "Gere orçamentos para o paciente avaliar o tratamento, com caixa separado entre clínica e profissional.",
    icon: ClipboardDocumentCheckIcon,
    tint: "bg-rose-100 text-rose-700",
  },
];

const modulos = [
  {
    icon: CalendarDaysIcon,
    title: "Agenda inteligente",
    subtitle: "Múltiplos profissionais e salas",
    items: [
      "Agendamentos recorrentes",
      "Registro de faltas",
      "Confirmação via WhatsApp",
    ],
  },
  {
    icon: DocumentTextIcon,
    title: "Prontuário eletrônico",
    subtitle: "Histórico completo do paciente",
    items: [
      "Anexos, imagens e exames",
      "Compartilhamento entre profissionais",
      "Alertas de saúde e financeiro",
    ],
  },
  {
    icon: BanknotesIcon,
    title: "Controle financeiro",
    subtitle: "Menos papel, mais controle",
    items: [
      "Receitas e despesas automáticas",
      "Caixa separado por profissional",
      "Relatórios personalizados",
    ],
  },
  {
    icon: ClipboardDocumentCheckIcon,
    title: "Orçamentos e caixa",
    subtitle: "Do orçamento ao pagamento",
    items: [
      "Orçamentos por paciente",
      "Controle de estoque de insumos",
      "Odontograma integrado",
    ],
  },
  {
    icon: ChatBubbleLeftRightIcon,
    title: "Integração interna",
    subtitle: "Recepção e consultório conectados",
    items: [
      "Comunicação discreta entre equipes",
      "Fila de espera automática",
      "Sem precisar consultar a recepção",
    ],
  },
  {
    icon: ClockIcon,
    title: "Controle de consultas",
    subtitle: "Gestão de horários simplificada",
    items: [
      "Histórico de atendimentos",
      "Planos e convênios",
      "Melhor gestão da agenda",
    ],
  },
  {
    icon: UserGroupIcon,
    title: "Cadastros e permissões",
    subtitle: "Equipe sob controle",
    items: [
      "Cadastro ilimitado de colaboradores",
      "Papéis e permissões por usuário",
      "Agenda individual",
    ],
  },
  {
    icon: ChartBarIcon,
    title: "Gestão inteligente",
    subtitle: "Dashboard em tempo real",
    items: [
      "Gráficos interativos",
      "Relatórios simples de interpretar",
      "Indicadores personalizados",
    ],
  },
];

useSeo({
  title: "Sistema para Clínicas Médicas e Odontológicas | Kali Clínica",
  description:
    "Agende, atenda e controle sua clínica com o Kali. Sistema completo com prontuário eletrônico, agenda inteligente e emissão de notas.",
  image: imageBg,
  schema: {
    "@context": "https://schema.org",
    "@type": ["SoftwareApplication", "LocalBusiness"],
    name: "Kali Clínica",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: "https://www.kalisistemas.com.br/solucao/kali-clinica",
    description:
      "Sistema de gestão completo para clínicas médicas, odontológicas e multiprofissionais, com agenda inteligente, prontuário eletrônico e emissão de notas fiscais.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "BRL",
    },
  },
});
</script>

<template>
  <div class="bg-white">
    <SolutionHero
      title="Kali Clínica: Sistema de Gestão para Clínicas Médicas e Odontológicas"
      description="Agende, atenda, registre e controle sua clínica com um sistema simples, completo e 100% online. Automatize processos e ganhe mais tempo para cuidar do que importa."
      primary-label="Solicite uma demonstração gratuita"
      primary-href="/contato?type=kali-clinica"
      :background-image="imageBg"
      background-class="bg-top bg-cover bg-slate-900"
      overlay-variant="left"
    >
    </SolutionHero>

    <section
      class="sticky top-16 z-40 border-b border-gray-100 shadow-sm backdrop-blur bg-white/95"
    >
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <nav
          class="flex overflow-x-auto relative gap-6 items-center h-16"
          aria-label="Navegação da página"
        >
          <a
            v-for="item in navLinks"
            :key="item.href"
            :href="item.href"
            class="relative inline-flex items-center px-2 h-16 text-sm font-bold text-gray-700 hover:text-[#006086] transition-all duration-300 whitespace-nowrap"
          >
            {{ item.label }}
          </a>
        </nav>
      </div>
    </section>

    <!-- Visão geral -->
    <section id="visao-geral" class="overflow-hidden relative py-16 bg-white scroll-mt-24 lg:min-h-[620px]">
      <div class="hidden absolute inset-y-0 right-0 w-1/2 lg:block">
        <img
          :src="visaoGeralImg"
          alt="Kali Clínica"
          class="object-cover object-center w-full h-full"
        />
      </div>
      <div class="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="lg:max-w-xl">
          <h2
            class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Feito para o sucesso do seu negócio
          </h2>
          <p class="mt-6 text-lg leading-8 text-gray-600">
            Quanto mais automatizada a rotina, mais tempo para cuidar do que
            importa. O Kali Clínica foi desenvolvido para facilitar a gestão
            de clínicas médicas, odontológicas e muitas outras especialidades,
            otimizando processos, organizando o fluxo de consultas com
            agendamento online e tornando a comunicação entre equipes mais
            eficiente — com gestão de plantão 24 horas.
          </p>
          <div class="flex gap-8 items-center mt-10 text-[#006086]">
            <DevicePhoneMobileIcon class="w-10 h-10" />
            <ComputerDesktopIcon class="w-10 h-10" />
            <DeviceTabletIcon class="w-10 h-10" />
          </div>
        </div>
      </div>
    </section>

    <!-- Segmentos -->
    <section id="segmentos" class="py-16 bg-gray-100 scroll-mt-24">
      <div class="grid gap-12 items-center px-4 mx-auto max-w-7xl sm:px-6 lg:px-8 lg:grid-cols-[1.3fr_1fr]">
        <div class="grid grid-cols-3 gap-x-6 gap-y-10 sm:grid-cols-4">
          <div
            v-for="s in segmentos"
            :key="s.label"
            v-reveal
            class="flex flex-col items-center text-center group"
          >
            <component
              :is="s.icon"
              class="w-11 h-11 text-kali-blue-600 transition-transform duration-200 ease-out group-hover:-translate-y-1"
            />
            <p
              class="mt-3 text-xs font-semibold text-gray-800 transition-colors duration-200 sm:text-sm group-hover:text-kali-blue-600"
            >
              {{ s.label }}
            </p>
          </div>
        </div>
        <div v-reveal>
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Soluções que atendem à realidade de cada
            <span class="text-kali-blue-600">especialidade</span>
            e rotina
          </h2>
          <p class="mt-4 text-base leading-7 text-gray-600">
            O Kali Clínica atende clínicas médicas, odontológicas e
            multiprofissionais, se adaptando à realidade
            operacional e às necessidades de cada especialidade.
          </p>
          <button
            type="button"
            @click="openDemoForSegmento"
            class="inline-flex justify-center items-center px-6 py-3 mt-6 text-base font-semibold text-white rounded-full border border-transparent shadow-sm bg-kali-blue-500 hover:bg-kali-blue-600"
          >
            Atendemos diversas especialidades da saúde
          </button>
        </div>
      </div>
    </section>

    <!-- Funcionalidades -->
    <section id="funcionalidades" class="py-20 bg-gradient-to-br from-white via-gray-50 to-[#e6f0f4] scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2 class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-4">
            Tudo que a sua clínica precisa, em um só sistema
          </h2>
          <p class="text-lg leading-8 text-gray-600">
            Da agenda ao financeiro, cada funcionalidade foi pensada para
            tirar trabalho manual do seu dia.
          </p>
        </div>
        <div class="grid gap-8 mt-14 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="d in destaques" :key="d.title" v-reveal>
            <div
              class="flex justify-center items-center w-full h-44 rounded-2xl"
              :class="d.tint"
            >
              <component :is="d.icon" class="w-14 h-14" />
            </div>
            <p class="mt-4 text-xs font-bold tracking-wide text-[#006086] uppercase">
              {{ d.eyebrow }}:
            </p>
            <h3 class="mt-1 text-lg font-bold text-gray-900">{{ d.title }}</h3>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ d.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Reforma tributária -->
    <section class="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div v-reveal class="overflow-hidden relative bg-kali-blue-600 rounded-[3rem] shadow-xl">
        <div class="grid gap-10 items-center px-8 py-12 lg:grid-cols-2 lg:px-16 lg:py-16">
          <div>
            <h2 class="text-2xl font-black tracking-tight text-white sm:text-3xl lg:text-4xl">
              Reforma tributária: é hora de se preparar!
            </h2>
            <p class="mt-4 text-base leading-7 text-kali-blue-100 sm:text-lg">
              O Kali Clínica acompanha as mudanças da reforma tributária em
              tempo real, mantendo sua clínica em conformidade sem parar de
              atender.
            </p>
            <a
              href="/reforma-tributaria"
              class="inline-flex justify-center items-center px-8 py-3 mt-8 text-sm font-bold tracking-wide text-kali-blue-700 uppercase bg-white rounded-full shadow-sm transition hover:bg-kali-blue-50"
            >
              Saiba mais
            </a>
          </div>
          <div class="hidden relative lg:block lg:h-80">
            <img
              :src="gestaoTributaria"
              alt="Especialistas em conformidade fiscal"
              class="object-cover absolute inset-0 w-4/5 h-full rounded-3xl shadow-lg"
            />
            <div class="flex absolute -top-6 left-4 justify-center items-center w-16 h-16 rounded-2xl shadow-lg bg-kali-gold">
              <DocumentCheckIcon class="w-8 h-8 text-white" />
            </div>
            <div class="flex absolute -bottom-6 right-4 justify-center items-center w-20 h-20 bg-cyan-400 rounded-2xl shadow-lg">
              <ScaleIconOutline class="w-9 h-9 text-white" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Fiscal -->
    <section id="fiscal" class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div class="grid gap-12 lg:grid-cols-2">
          <div v-reveal>
            <h2 class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-3">
              Documentos fiscais
            </h2>
            <p class="mb-6 text-lg leading-8 text-gray-600">
              Emita documentos fiscais de forma descomplicada, integrada à
              maioria das prefeituras do Brasil.
            </p>
            <div class="flex justify-center items-center py-6 text-2xl font-bold rounded-lg bg-kali-blue-100 text-kali-blue-700">
              NFS-e
            </div>
            <ul class="mt-6 space-y-2 text-sm text-gray-700">
              <li>
                <span class="font-semibold text-gray-900">Entrada de Notas —</span>
                através de XML.
              </li>
              <li>
                <span class="font-semibold text-gray-900">Integração —</span>
                com a maioria das prefeituras do Brasil, atendendo a legislação
                de todos os estados.
              </li>
            </ul>
          </div>
          <div v-reveal>
            <h2 class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-3">
              Integração e permissões
            </h2>
            <p class="mb-6 text-lg leading-8 text-gray-600">
              Comunicação discreta entre médicos e secretários, e controle
              completo de quem acessa o quê na sua clínica.
            </p>
            <ul class="space-y-3 text-sm text-gray-700">
              <li>
                <span class="font-semibold text-gray-900">Fila de espera automática —</span>
                o profissional acompanha da sua sala, sem consultar a recepção.
              </li>
              <li>
                <span class="font-semibold text-gray-900">Cadastro ilimitado —</span>
                defina o papel de cada colaborador e suas funcionalidades.
              </li>
              <li>
                <span class="font-semibold text-gray-900">Agenda individual —</span>
                cada usuário gerencia a própria agenda.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Módulos -->
    <section id="modulos" class="py-16 bg-gradient-to-br from-white via-gray-50 to-[#e6f0f4] scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <h2
          class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-4"
        >
          Módulos do Kali Clínica
        </h2>
        <p class="mt-4 mb-12 max-w-2xl text-lg leading-8 text-gray-600">
          Conecte o Kali Clínica a módulos adicionais e leve a gestão da sua
          clínica a outro nível.
        </p>
        <div class="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="m in modulos" :key="m.title" v-reveal class="text-center">
            <component :is="m.icon" class="mx-auto w-10 h-10 text-[#006086]" />
            <h3 class="mt-4 text-lg font-bold text-gray-900">{{ m.title }}</h3>
            <p class="mt-1 text-sm font-semibold text-gray-700">{{ m.subtitle }}</p>
            <ul class="mt-4 space-y-2 text-sm text-gray-600">
              <li v-for="it in m.items" :key="it">{{ it }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Depoimentos -->
    <section id="depoimentos" class="scroll-mt-24">
      <Clients />
    </section>

    <section id="duvidas" class="scroll-mt-24">
      <FaqPanel
        :items="[
          {
            question: 'Tem agenda com confirmação automática?',
            answer:
              'Sim. Lembretes por WhatsApp/e-mail e confirmação com retorno no sistema.',
          },
          {
            question: 'Possui prontuário eletrônico?',
            answer:
              'Sim. Registros completos por paciente, com anexos e histórico.',
          },
          {
            question: 'Emite NFS-e para serviços médicos?',
            answer:
              'Sim. Emissão de NFS-e conforme a prefeitura, integrada ao financeiro.',
          },
          {
            question: 'Funciona multiunidade?',
            answer:
              'Sim. Controle de profissionais, salas e filiais com permissões por usuário.',
          },
        ]"
        description="Dúvidas sobre Kali Clínica? Veja as respostas e fale com nosso time."
        cta-label="Falar com Consultor"
        :cta-opens-modal="true"
      />
    </section>

    <section id="fale-conosco" class="scroll-mt-16">
      <CTA />
    </section>
  </div>
</template>

<style scoped>
.reveal-init {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.reveal-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal-init {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
